import {
  getTheKybCountryCodeByCountryName,
  THE_KYB_COUNTRY_CODES,
} from "@/components/shared/constants/general.const";
import {
  getCountryNameFromIsoCode,
  getIsoCountryCodeFromHeadquarters,
} from "@shared/worldCountryIsoCodes";
import "./CountryFlag.css";

export function isoAlpha2FromHeadquartersCountry(countryName?: string | null): string | null {
  const n = countryName?.trim();
  if (!n) return null;
  const row = THE_KYB_COUNTRY_CODES.find((c) => c.name === n);
  if (row) return row.code;
  const kybCode = getTheKybCountryCodeByCountryName(n);
  if (kybCode) return kybCode;
  return getIsoCountryCodeFromHeadquarters(n);
}

export function countryHasRenderableFlag(country?: string | null): boolean {
  const iso = isoAlpha2FromHeadquartersCountry(country);
  return Boolean(iso && iso.length === 2 && /^[A-Z]{2}$/.test(iso));
}

function flagImageUrl(alpha2: string): string {
  return `https://flagcdn.com/w40/${alpha2.toLowerCase()}.png`;
}

type CountryFlagProps = {
  /** Headquarters country display name */
  country?: string | null;
  /** ISO 3166-1 alpha-2 code (takes precedence when both are provided) */
  alpha2?: string | null;
  className?: string;
  title?: string;
  shape?: "rect" | "circle";
  size?: "sm" | "lg";
};

export default function CountryFlag({
  country,
  alpha2,
  className,
  title,
  shape = "rect",
  size = "sm",
}: CountryFlagProps) {
  const iso =
    alpha2?.trim().toUpperCase() ||
    (country ? isoAlpha2FromHeadquartersCountry(country) : null);

  if (!iso || iso.length !== 2 || !/^[A-Z]{2}$/.test(iso)) {
    return null;
  }

  const countryName =
    country?.trim() ||
    THE_KYB_COUNTRY_CODES.find((c) => c.code === iso)?.name ||
    getCountryNameFromIsoCode(iso) ||
    null;

  const tooltip = title ?? countryName ?? iso;
  const ariaLabel = countryName ? `${countryName} flag` : `${iso} flag`;

  const shapeClass = shape === "circle" ? "gt-country-flag--circle" : "";
  const sizeClass = size === "lg" ? "gt-country-flag--lg" : "";
  const classes = ["gt-country-flag", shapeClass, sizeClass, className].filter(Boolean).join(" ");

  return (
    <span
      className={classes}
      role="img"
      aria-label={ariaLabel}
      title={tooltip}
    >
      <img src={flagImageUrl(iso)} alt="" loading="lazy" />
    </span>
  );
}
